import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, ArrowRight, Phone, MapPin, Clock, Star, Shield, Paintbrush } from 'lucide-react';
import { services, cities } from '../data/seoData';
import { getServiceContent } from '../data/serviceContent';

export default function ServiceCityPage() {
  const location = useLocation();
  const path = location.pathname.replace('/', '');
  
  // Parse the path: e.g., "innenanstrich-duesseldorf" -> service: innenanstrich, city: duesseldorf
  let service = null;
  let city = null;
  
  // Try to find matching service and city
  for (const serviceKey of Object.keys(services)) {
    for (const cityKey of Object.keys(cities)) {
      if (path === `${serviceKey}-${cityKey}`) {
        service = services[serviceKey];
        city = cities[cityKey];
        break;
      }
    }
    if (service && city) break;
  }
  
  if (!service || !city) {
    return <NotFoundContent />;
  }
  
  // Get unique content for this service-city combination
  const content = getServiceContent(service.slug, city.slug);
  
  // Use unique content or fallback to generic
  const heroTitle = content?.heroTitle || `${service.name} in ${city.name}`;
  const heroSubtitle = content?.heroSubtitle || `Professionelle ${service.name} vom Fachbetrieb. Qualität, faire Preise und Termintreue in ${city.name}.`;
  const mainContent = content?.mainContent || null;
  const faqItems = content?.faq || getDefaultFaq(service.name, city.name);
  const metaInfo = content?.meta || {
    title: `${service.name} ${city.name} | FarbWelt Service`,
    description: `Professionelle ${service.name} in ${city.name}. ✓ Faire Preise ✓ Qualitätsarbeit ✓ Termingerecht. Jetzt Beratungstermin buchen!`,
    keywords: `${service.name} ${city.name}, ${service.slug} ${city.slug}, Maler ${city.name}`
  };
  
  // Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${service.name} in ${city.name}`,
    "description": metaInfo.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": "FarbWelt Maler Express Service UG (Haftungsbeschränkt)",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city.name,
        "addressRegion": "Nordrhein-Westfalen",
        "addressCountry": "DE"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": city.name
    }
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
  
  return (
    <>
      <Helmet>
        <title>{metaInfo.title}</title>
        <meta name="description" content={metaInfo.description} />
        <meta name="keywords" content={metaInfo.keywords} />
        <link rel="canonical" href={`https://www.farbwelt-service.de/${service.slug}-${city.slug}`} />
        <meta property="og:title" content={metaInfo.title} />
        <meta property="og:description" content={metaInfo.description} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#2E2A88] via-[#1e1a5e] to-[#9B1B30] text-white py-20 lg:py-28">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            {/* Breadcrumb */}
            <nav className="text-sm mb-6 text-white/80" aria-label="Breadcrumb">
              <ol className="flex items-center flex-wrap">
                <li>
                  <Link to="/" className="hover:text-white">Home</Link>
                </li>
                <span className="mx-2">/</span>
                <li>
                  <Link to={`/leistungen/${service.slug}`} className="hover:text-white">{service.name}</Link>
                </li>
                <span className="mx-2">/</span>
                <li>
                  <span>{city.name}</span>
                </li>
              </ol>
            </nav>
            
            <div className="max-w-4xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-barlow mb-6 leading-tight">
                {heroTitle}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 font-manrope leading-relaxed">
                {heroSubtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/termin" data-testid="hero-cta-beratung">
                  <Button size="lg" className="bg-[#9B1B30] hover:bg-[#7a1525] text-white px-8 py-6 text-lg w-full sm:w-auto">
                    <Phone className="w-5 h-5 mr-2" />
                    Kostenlose Beratung
                  </Button>
                </Link>
                <Link to="/termin" data-testid="hero-cta-termin">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg w-full sm:w-auto">
                    Termin buchen
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="bg-gray-50 py-8 border-b">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Star className="w-8 h-8 text-[#F59E0B] mb-2" />
                <span className="font-bold text-lg">Top Bewertungen</span>
                <span className="text-sm text-gray-600">Zufriedene Kunden</span>
              </div>
              <div className="flex flex-col items-center">
                <Clock className="w-8 h-8 text-[#9B1B30] mb-2" />
                <span className="font-bold text-lg">Termingerecht</span>
                <span className="text-sm text-gray-600">Pünktliche Ausführung</span>
              </div>
              <div className="flex flex-col items-center">
                <Shield className="w-8 h-8 text-green-600 mb-2" />
                <span className="font-bold text-lg">Festpreise</span>
                <span className="text-sm text-gray-600">Keine versteckten Kosten</span>
              </div>
              <div className="flex flex-col items-center">
                <Paintbrush className="w-8 h-8 text-[#2E2A88] mb-2" />
                <span className="font-bold text-lg">Premium-Qualität</span>
                <span className="text-sm text-gray-600">Hochwertige Materialien</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        {mainContent && (
          <section className="py-16 lg:py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <article className="prose prose-lg max-w-none text-gray-700">
                  {/* Intro */}
                  <div className="mb-8">
                    {mainContent.intro.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-4 leading-relaxed">{paragraph.trim()}</p>
                    ))}
                  </div>

                  {/* Expertise Section */}
                  {mainContent.expertiseSection && (
                    <>
                      <h2 className="text-2xl md:text-3xl font-bold font-barlow text-gray-900 mt-12 mb-6">
                        {mainContent.expertiseSection.title}
                      </h2>
                      <div className="mb-8">
                        {mainContent.expertiseSection.content.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className="mb-4 leading-relaxed">{paragraph.trim()}</p>
                        ))}
                      </div>
                    </>
                  )}
                </article>
              </div>
            </div>
          </section>
        )}

        {/* Process Section */}
        {mainContent?.processSection && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold font-barlow mb-12 text-center">
                {mainContent.processSection.title}
              </h2>
              <div className="max-w-4xl mx-auto">
                <div className="space-y-6">
                  {mainContent.processSection.steps.map((step, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#9B1B30] text-white rounded-full flex items-center justify-center font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Specialties/Services Section */}
        {(mainContent?.specialtiesSection || mainContent?.servicesSection || mainContent?.tapetenTypen || mainContent?.packageSection) && (
          <section className="py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold font-barlow mb-12 text-center">
                {mainContent.specialtiesSection?.title || mainContent.servicesSection?.title || mainContent.tapetenTypen?.title || mainContent.packageSection?.title}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(mainContent.specialtiesSection?.items || mainContent.servicesSection?.items || mainContent.tapetenTypen?.items || mainContent.packageSection?.items)?.map((item, idx) => (
                  <Card key={idx} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="w-10 h-10 bg-[#9B1B30]/10 rounded-lg flex items-center justify-center mb-4">
                        <Check className="w-5 h-5 text-[#9B1B30]" />
                      </div>
                      <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Pricing Section */}
        {mainContent?.pricingSection && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold font-barlow mb-6">
                  {mainContent.pricingSection.title}
                </h2>
                <div className="prose prose-lg max-w-none text-gray-700">
                  {mainContent.pricingSection.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="mb-4 whitespace-pre-line">{paragraph.trim()}</p>
                  ))}
                </div>
                <div className="mt-8">
                  <Link to="/termin">
                    <Button className="bg-[#9B1B30] hover:bg-[#7a1525]">
                      Kostenloses Angebot anfordern
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Benefits Section (for generic pages) */}
        {!mainContent && (
          <section className="py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold font-barlow mb-8 text-center">
                Ihre Vorteile bei FarbWelt Service
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {service.benefits.map((benefit, idx) => (
                  <Card key={idx} className="text-center">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="font-medium">{benefit}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Districts Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold font-barlow mb-8 text-center">
              {service.name} in ganz {city.name}
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir sind in allen Stadtteilen von {city.name} für Sie da. Klicken Sie auf einen Stadtteil für mehr Informationen.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {city.districts.slice(0, 16).map((district) => (
                <Link
                  key={district.slug}
                  to={`/maler-${city.slug}-${district.slug}`}
                  className="p-4 bg-gray-50 border rounded-lg hover:border-[#9B1B30] hover:bg-white hover:shadow-md transition-all text-center group"
                >
                  <MapPin className="w-5 h-5 text-[#9B1B30] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm">{district.name}</span>
                </Link>
              ))}
            </div>
            {city.districts.length > 16 && (
              <div className="text-center mt-6">
                <Link to={`/maler-${city.slug}`} className="text-[#9B1B30] font-medium hover:underline">
                  Alle {city.districts.length} Stadtteile anzeigen →
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold font-barlow mb-8 text-center">
              Häufige Fragen zu {service.name} in {city.name}
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqItems.map((faq, idx) => (
                <div key={idx} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="font-bold text-lg mb-2 text-gray-900">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#2E2A88] text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-barlow mb-4">
              Bereit für professionelle {service.name}?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Buchen Sie jetzt Ihren kostenlosen Besichtigungstermin in {city.name}. 
              Wir beraten Sie vor Ort und erstellen ein unverbindliches Angebot.
            </p>
            <Link to="/termin" data-testid="cta-termin-buchen">
              <Button size="lg" className="bg-[#9B1B30] hover:bg-[#7a1525] text-white px-12 py-6 text-lg">
                Jetzt Termin buchen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Other Services */}
        <section className="py-12 border-t">
          <div className="container mx-auto px-4">
            {/* Prominenter Link zur Hub-Seite */}
            <div className="mb-8 p-4 bg-[#9B1B30]/5 rounded-lg border border-[#9B1B30]/20">
              <Link 
                to={`/maler-${city.slug}`}
                className="inline-flex items-center text-[#9B1B30] font-medium hover:underline"
                data-testid="back-to-hub-link"
              >
                <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                Alle Malerleistungen in {city.name} ansehen
              </Link>
              <p className="text-sm text-gray-600 mt-1">
                Entdecken Sie unser komplettes Leistungsangebot für {city.name} und alle Stadtteile.
              </p>
            </div>
            
            <h3 className="text-lg font-bold mb-4">Weitere Leistungen in {city.name}:</h3>
            <div className="flex flex-wrap gap-4">
              {Object.values(services).filter(s => s.slug !== service.slug).map((s) => (
                <Link
                  key={s.slug}
                  to={`/${s.slug}-${city.slug}`}
                  className="text-[#9B1B30] hover:underline"
                  data-testid={`other-service-link-${s.slug}`}
                >
                  {s.name} {city.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Other Cities */}
        <section className="py-8 border-t bg-gray-50">
          <div className="container mx-auto px-4">
            <h3 className="text-lg font-bold mb-4">{service.name} auch in:</h3>
            <div className="flex flex-wrap gap-4">
              {Object.values(cities).filter(c => c.slug !== city.slug).map((c) => (
                <Link
                  key={c.slug}
                  to={`/${service.slug}-${c.slug}`}
                  className="text-gray-600 hover:text-[#9B1B30]"
                >
                  {service.name} {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

// Default FAQ for services without unique content
function getDefaultFaq(serviceName, cityName) {
  return [
    {
      question: `Was kostet ${serviceName} in ${cityName}?`,
      answer: `Die Kosten hängen vom Umfang und den Gegebenheiten vor Ort ab. Wir erstellen Ihnen gerne ein kostenloses Angebot nach einer Besichtigung.`
    },
    {
      question: `Wie schnell können Sie mit ${serviceName} in ${cityName} beginnen?`,
      answer: `In der Regel können wir innerhalb von 1-2 Wochen mit den Arbeiten beginnen. Bei dringenden Projekten bieten wir auch Express-Termine an.`
    },
    {
      question: `Arbeiten Sie in allen Stadtteilen von ${cityName}?`,
      answer: `Ja, wir sind in ganz ${cityName} und allen Stadtteilen tätig. Kurze Anfahrtswege garantieren schnelle Verfügbarkeit.`
    },
    {
      question: `Welche Materialien verwenden Sie?`,
      answer: `Wir arbeiten ausschließlich mit hochwertigen Produkten namhafter Hersteller. Die Materialqualität ist ein wichtiger Faktor für ein langlebiges Ergebnis.`
    }
  ];
}

// 404 Content
function NotFoundContent() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center px-4">
        <h1 className="text-4xl font-bold mb-4">Seite nicht gefunden</h1>
        <p className="text-gray-600 mb-8">Diese Kombination aus Service und Stadt existiert nicht.</p>
        <div className="space-x-4">
          <Link to="/">
            <Button>Zurück zur Startseite</Button>
          </Link>
          <Link to="/leistungen">
            <Button variant="outline">Alle Leistungen</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
